import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import axios from 'axios'
import {
  fetchPDFDataWithCoordinates,
  fetchPDFDataWithOutCoordinates,
  transformDataWithCoordinates,
  transformDataWithOutCoordinates
} from '../../utils/Api'

// PDF.js worker setup
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const PdfJsonViewer = ({
  closeDialog,
  currentPdfLink,
  docId,
  applicationId
}) => {
  const [textPositions, setTextPositions] = useState([])
  const [jsonData, setJsonData] = useState([])
  const jsonEditorRef = useRef(null) // Reference for the JSON editor section
  const pdfViewerRef = useRef(null) // Reference for the PDF viewer section
  const [numPages, setNumPages] = useState(0) // Track total pages
  const pdfPageRef = useRef(null)
  const [newFieldKey, setNewFieldKey] = useState('')
  const [newFieldValue, setNewFieldValue] = useState('')
  const [showNewField, setShowNewField] = useState(false)
  const [activePage, setActivePage] = useState(1) // Track active page number
  const [changedProperties, setChangedProperties] = useState([])

  useEffect(() => {
    const fetchData = async () => {
      const textPositions = await fetchPDFDataWithCoordinates({
        pageNumber: activePage || 1,
        doc_id: docId,
        application_id: applicationId
      })
      const jsonDataWithoutCoordinates = await fetchPDFDataWithOutCoordinates({
        pageNumber: activePage || 1,
        doc_id: docId,
        application_id: applicationId
      })
      setTextPositions(transformDataWithCoordinates(textPositions))
      setJsonData(
        transformDataWithOutCoordinates(jsonDataWithoutCoordinates)[0]
      )
    }
    fetchData()
  }, [activePage])

  const handleJsonClick = (key, event, fromJson) => {
    const targetElement = document.getElementById(key)

    if (targetElement && jsonEditorRef.current) {
      setTimeout(() => {
        targetElement.style.backgroundColor = 'rgba(0, 255, 0, 0.3)' // Highlight with green color
      }, 0)
      jsonEditorRef.current.scrollTo({
        top: targetElement.offsetTop - 70,
        behavior: 'smooth'
      })
    }
    const field = textPositions.find(item => item[key])
    if (field && field[key].coordinates && pdfViewerRef.current) {
      pdfViewerRef.current.scrollTo({
        top: field[key].coordinates.y1 - 50, // Offset to position the field better
        behavior: 'smooth'
      })
    }
    if (event) {
      const overlays = document.querySelectorAll('.overlay')
      const jsonKeys = document.querySelectorAll('.json-key')
      overlays.forEach(overlay => {
        overlay.style.backgroundColor = 'rgba(255, 255, 0, 0.3)' // Reset color
      })
      jsonKeys.forEach(jsonKey => {
        jsonKey.style.backgroundColor = 'white' // Reset color
      })
      const targetElement = document.getElementById(`json-key-${key}`)
      if (targetElement) {
        targetElement.style.backgroundColor = 'rgba(0, 255, 0, 0.3)' // Highlight with green color
      }
      const targetElement2 = document.getElementById(`${key}`)
      if (targetElement2) {
        targetElement2.style.backgroundColor = 'rgba(0, 255, 0, 0.3)' // Highlight with green color
      }
    }
  }

  const handleCancel = () => {
    closeDialog()
  }

  const handleJsonChange = (keyPath, value, prop_id) => {
    const key = keyPath.join('.')
    setJsonData(prevData => {
      const updatedData = { ...prevData }
      let current = updatedData
      for (let i = 0; i < keyPath.length - 1; i++) {
        current = current[keyPath[i]]
      }
      current[keyPath[keyPath.length - 1]] = `${value}$$${prop_id}`
      return updatedData
    })

    setChangedProperties(prevChangedProperties => {
      const updatedProperties = [...prevChangedProperties]
      const existingIndex = updatedProperties.findIndex(
        item => item.key === key && item.id === prop_id
      )
      if (existingIndex !== -1) {
        updatedProperties[existingIndex].value = value
      } else {
        updatedProperties.push({ key, value, id: prop_id })
      }
      return updatedProperties
    })
  }

  const handleReset = () => {
    //setJsonData(jsonContent)
  }

  const handleUpdate = async () => {
    const accessToken = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')
    const headers = {
      'X-Access-Token': accessToken,
      'X-Refresh-Token': refreshToken,
      Accept: 'application/json'
    }
    const updatePromises = changedProperties.map(async property => {
      const { key, value, id } = property
      const apiUrl = `https://insureai-intake-dev-1074099157700.us-central1.run.app/application_mapping/mappings/${id}`
      try {
        const response = await axios.put(
          apiUrl,
          {
            key,
            value
          },
          {
            headers
          }
        )
        console.log(`Data for ${key} saved successfully:`, response.data)
      } catch (error) {
        console.error(`Error saving data for ${key}:`, error)
      }
    })

    await Promise.all(updatePromises)
  }

  const handleAddField = () => {
    setShowNewField(!showNewField)
    if (newFieldKey && newFieldValue) {
      setJsonData(prevData => ({
        ...prevData,
        [newFieldKey]: newFieldValue
      }))
      setNewFieldKey('')
      setNewFieldValue('')
    }
  }

  const renderJson = (data, keyPath = []) => {
    return Object.keys(data || {}).map(key => {
      const currentPath = [...keyPath, key]
      let value = data[key]
      let prop_id = ''
      if (typeof value === 'string' && value !== null) {
        value = data[key].split('$$')[0]
        prop_id = data[key].split('$$')[1]
      }

      return (
        <div
          id={`json-key-${key}`}
          key={currentPath.join('.')}
          style={{ paddingLeft: keyPath.length * 20 }}
          className={`json-key`}
          onClick={e => handleJsonClick(currentPath.join('.'), e, true)}
        >
          <strong>{key}:</strong>{' '}
          {typeof value === 'object' && value !== null ? (
            <div style={{ paddingLeft: 20 }}>
              {renderJson(value, currentPath)}
            </div>
          ) : (
            <input
              type='text'
              value={value}
              onChange={e =>
                handleJsonChange(currentPath, e.target.value, prop_id)
              }
            />
          )}
        </div>
      )
    })
  }

  const memoizedDocument = useMemo(
    () => (
      <Document
        file={{
          url: currentPdfLink
        }}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
      >
        <Page
          pageNumber={activePage}
          inputRef={pdfPageRef}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
        <Page pageNumber={activePage} inputRef={pdfPageRef} />
        {textPositions.map((item, overlayIndex) =>
          Object.keys(item).map(key => {
            if (key !== 'pageNumber' && item[key].coordinates) {
              return (
                <div
                  id={key}
                  key={`${overlayIndex}-${key}`}
                  className='overlay'
                  onClick={e => handleJsonClick(key, e, false)}
                  style={{
                    position: 'absolute',
                    left: item[key].coordinates.x1,
                    top: item[key].coordinates.y1,
                    width: item[key].coordinates.x2 - item[key].coordinates.x1,
                    height: item[key].coordinates.y2 - item[key].coordinates.y1,
                    backgroundColor: 'rgba(255, 255, 0, 0.3)', // Change color when active // Default color
                    cursor: 'pointer'
                  }}
                />
              )
            } else if (Array.isArray(item[key])) {
              return item[key].map((line, lineIndex) => (
                <div
                  id={`${key}-${lineIndex}`}
                  key={`${overlayIndex}-${key}-${lineIndex}`}
                  className='overlay'
                  onClick={e => handleJsonClick(`${key}-${lineIndex}`, e)}
                  style={{
                    position: 'absolute',
                    left: line.coordinates.x1,
                    top: line.coordinates.y1,
                    width: line.coordinates.x2 - line.coordinates.x1,
                    height: line.coordinates.y2 - line.coordinates.y1,
                    cursor: 'pointer'
                  }}
                />
              ))
            }
            return null
          })
        )}
      </Document>
    ),
    [activePage, textPositions]
  )

  const handlePreviousPage = () => {
    setActivePage(prevPage => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    setActivePage(prevPage => Math.min(prevPage + 1, numPages))
  }

  return (
    <>
      <div style={{ display: 'flex', height: '70vh' }}>
        <div
          ref={pdfViewerRef}
          style={{
            background: '#fff',
            position: 'relative',
            width: '55%',
            overflow: 'auto',
            border: '1px solid #ccc'
          }}
        >
          {memoizedDocument}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '10px'
            }}
          >
            <button
              className='pagination-button'
              onClick={handlePreviousPage}
              disabled={activePage === 1}
            >
              Previous
            </button>
            <span style={{ margin: '0 10px' }}>
              Page {activePage} of {numPages}
            </span>
            <input
              type='number'
              value={activePage}
              onChange={e => setActivePage(Number(e.target.value))}
              min='1'
              max={numPages}
              style={{ width: '50px', textAlign: 'center' }}
            />
            <button
              className='pagination-button'
              onClick={handleNextPage}
              disabled={activePage === numPages}
            >
              Next
            </button>
          </div>
        </div>
        <div
          className='json-section'
          style={{
            overflow: 'auto',
            marginLeft: '5px',
            padding: '15px'
          }}
          ref={jsonEditorRef} // Add ref for scrolling
        >
          <div style={{ marginTop: '20px' }}>
            <div style={{ float: 'right' }}>
              <button
                onClick={handleAddField}
                style={{
                  padding: '5px 15px',
                  backgroundColor: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Add Field
              </button>
            </div>
            {showNewField && (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type='text'
                    placeholder='Enter key'
                    value={newFieldKey}
                    onChange={e => setNewFieldKey(e.target.value)}
                    style={{
                      padding: '10px',
                      marginRight: '10px',
                      border: '1px solid #ccc',
                      height: '30px'
                    }}
                  />
                  <input
                    type='text'
                    placeholder='Enter value'
                    value={newFieldValue}
                    onChange={e => setNewFieldValue(e.target.value)}
                    style={{
                      padding: '10px',
                      border: '1px solid #ccc',
                      height: '30px'
                    }}
                  />
                </div>
              </>
            )}
          </div>
          {renderJson(jsonData)}
        </div>
      </div>
      <div
        style={{
          margin: '20px 0',
          float: 'right'
        }}
      >
        <button
          onClick={handleUpdate}
          style={{
            padding: '5px 15px',
            backgroundColor: 'green',
            color: 'white',
            border: '1px solid green',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Save Data
        </button>
        <button
          onClick={handleReset}
          style={{
            padding: '5px 15px',
            backgroundColor: 'rgb(234, 88, 12)',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            margin: '0 10px'
          }}
        >
          Reset
        </button>
        <button
          onClick={handleCancel}
          style={{
            padding: '5px 15px',
            backgroundColor: 'red',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </>
  )
}

export default PdfJsonViewer
